import { Controller } from "@hotwired/stimulus"

declare const Stripe: any

export default class extends Controller {
  connect() {
    // Initialize quantity in form on page load
    const quantityInput = this.ensureQuantityInput()
    console.log('[PageComponent] Initialized quantity input:', quantityInput?.value)
  }

  updateQuantity(event: CustomEvent) {
    const detail = event.detail || {}
    const quantity = Number(detail.quantity)
    const credits = Number(detail.credits)
    const totalCents = Number(detail.totalCents)

    // Update form quantity hidden input
    const quantityInput = this.ensureQuantityInput()
    if (quantityInput && Number.isFinite(quantity)) {
      quantityInput.value = String(quantity)
    }

    // Update pay_with_existing_card link href
    const cardLink = document.getElementById('pay_with_existing_card_button') as HTMLAnchorElement
    if (cardLink) {
      // Replace quantity param in href
      const url = new URL(cardLink.href, window.location.origin)
      if (Number.isFinite(quantity)) {
        url.searchParams.set('quantity', String(quantity))
      }

      if (Number.isFinite(credits)) {
        url.searchParams.set('credits', String(credits))
      }

      cardLink.href = url.toString()
    }

    // Update amount-value on transaction component
    const transactionComponent = document.querySelector('[data-payments--stripe--transaction-component-amount-value]')
    if (transactionComponent && Number.isFinite(totalCents)) {
      transactionComponent.setAttribute('data-payments--stripe--transaction-component-amount-value', String(totalCents))
    }

    // Update amount-value on address component
    const addressComponent = document.querySelector('[data-payments--stripe--address-component-amount-value]')
    if (addressComponent && Number.isFinite(totalCents)) {
      addressComponent.setAttribute('data-payments--stripe--address-component-amount-value', String(totalCents))
    }
  }

  handleExistingCardClick(event: MouseEvent) {
    const link = (event.target as Element).closest('#pay_with_existing_card_button') as HTMLAnchorElement | null
    if (!link) return
    // Only intercept non-remote links (remote: true uses Rails UJS and loads address form)
    if (link.dataset.remote === 'true') return

    event.preventDefault()
    event.stopPropagation()
    this.postExistingCard(link)
  }

  private async postExistingCard(link: HTMLAnchorElement): Promise<void> {
    const originalText = link.textContent
    link.textContent = 'Charging card...'
    link.style.pointerEvents = 'none'

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''

    try {
      const response = await fetch(link.href, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      const data = await response.json()

      if (data.status === 'success') {
        window.location.href = data.redirect_url
        return
      }

      if (data.status === 'requires_action') {
        await this.handle3dsAuthentication(data.client_secret, data.payment_intent_id, data.confirm_url)
        return
      }

      if (data.status === 'error') {
        this.showPaymentError(data.message || 'Payment failed. Please try again.')
      }
    } catch (error) {
      console.error('[PageComponent] Existing card payment failed')
      this.showPaymentError('An error occurred while processing your payment. Please try again.')
    } finally {
      link.textContent = originalText
      link.style.pointerEvents = ''
    }
  }

  private async handle3dsAuthentication(clientSecret: string, paymentIntentId: string, confirmUrl: string): Promise<void> {
    const stripeKey = this.getStripePublishableKey()
    if (!stripeKey) {
      this.showPaymentError('Payment authentication is not available. Please refresh and try again.')
      return
    }

    const stripe = Stripe(stripeKey)
    const { error } = await stripe.handleNextAction({ clientSecret })

    if (error) {
      console.error('[PageComponent] 3DS authentication failed')
    }

    // Always confirm — backend is source of truth on PaymentIntent status
    await this.confirmPayment(paymentIntentId, confirmUrl)
  }

  private async confirmPayment(paymentIntentId: string, confirmUrl: string): Promise<void> {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''

    try {
      const response = await fetch(confirmUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({ payment_intent_id: paymentIntentId })
      })

      const data = await response.json()

      if (data.status === 'success') {
        window.location.href = data.redirect_url
      } else {
        this.showPaymentError(data.message || 'Payment confirmation failed. Please try again.')
      }
    } catch (error) {
      console.error('[PageComponent] Payment confirmation failed')
      this.showPaymentError('Payment confirmation failed. Please try again.')
    }
  }

  private getStripePublishableKey(): string | null {
    // Read from the TransactionComponent or AddressOnlyTransactionComponent already on the page
    const el = document.querySelector('[data-payments--stripe--transaction-component-stripe-publishable-key-value]')
    if (el) return el.getAttribute('data-payments--stripe--transaction-component-stripe-publishable-key-value')

    // Fallback: check if Stripe was already loaded with a key on the card component
    const cardEl = document.querySelector('[data-payments--stripe--card-component-stripe-publishable-key-value]')
    if (cardEl) return cardEl.getAttribute('data-payments--stripe--card-component-stripe-publishable-key-value')

    // Last resort: check the form's data attribute
    const formEl = document.querySelector('[data-stripe-publishable-key]') as HTMLElement
    if (formEl) return formEl.getAttribute('data-stripe-publishable-key')

    return null
  }

  private showPaymentError(message: string): void {
    const flashContainer = document.querySelector('.flash-messages, .alert-container, [data-flash-target]')
    if (flashContainer) {
      flashContainer.innerHTML = `<div class="alert alert-danger">${message}</div>`
      flashContainer.scrollIntoView({ behavior: 'smooth', block: 'center' })
    } else {
      alert(message)
    }
  }

  private ensureQuantityInput(): HTMLInputElement | null {
    const form = document.getElementById('payment_form') as HTMLFormElement
    if (!form) return null

    let quantityInput = form.querySelector('input[name="quantity"]') as HTMLInputElement
    if (!quantityInput) {
      // Get initial quantity from stepper input
      const stepperInput = document.querySelector('input[name="quantity"][readonly]') as HTMLInputElement
      const initialQuantity = stepperInput?.value || '2'

      quantityInput = document.createElement('input')
      quantityInput.type = 'hidden'
      quantityInput.name = 'quantity'
      quantityInput.value = initialQuantity
      form.appendChild(quantityInput)
    }
    return quantityInput
  }
}
